// @ts-check
/** @typedef {import('../types/api').TeamWorkload} TeamWorkload */

import { TrendingDown, TrendingUp, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import taskService from '../services/taskService';

const TeamWorkloadPage = () => {
  /** @type {[TeamWorkload[], Function]} */
  const [workload, setWorkload] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWorkload();
  }, []);

  const loadWorkload = async () => {
    setLoading(true);
    try {
      const data = await taskService.getTeamWorkload();
      setWorkload(data);
    } catch (error) {
      console.error('Error loading workload:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWorkloadColor = (total) => {
    if (total === 0) return 'bg-accent';
    if (total <= 3) return 'bg-green-200 dark:bg-green-900/60';
    if (total <= 7) return 'bg-yellow-200 dark:bg-yellow-900/60';
    if (total <= 12) return 'bg-orange-200 dark:bg-orange-900/60';
    return 'bg-red-200 dark:bg-red-900/60';
  };

  return (
    <div className="min-h-screen bg-muted/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Team Workload</h1>
          <p className="mt-2 text-sm text-muted-foreground">View and balance task distribution across team members</p>
        </div>

        {loading ? (
          <LoadingScreen />
        ) : workload.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-lg border">
            <Users size={48} className="mx-auto text-muted-foreground/70 mb-4" />
            <p className="text-muted-foreground">No team members found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {workload.map((member) => {
              const totalTasks = member.total_tasks;
              const inProgress = member.status_counts?.in_progress || 0;
              const toDo = member.status_counts?.to_do || 0;

              return (
                <div
                  key={member.user_id}
                  className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  {/* Header */}
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center text-xl font-bold">
                        {member.user_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">{member.user_name}</h3>
                        <p className="text-xs text-muted-foreground">{member.user_email}</p>
                      </div>
                    </div>
                    <div
                      className={`w-12 h-12 ${getWorkloadColor(totalTasks)} rounded-full flex items-center justify-center font-bold text-foreground`}
                    >
                      {totalTasks}
                    </div>
                  </div>

                  {/* Workload Bar */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span>Workload</span>
                      <span>{totalTasks} tasks</span>
                    </div>
                    <div className="w-full bg-accent rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          totalTasks === 0
                            ? 'bg-muted-foreground/30'
                            : totalTasks <= 3
                              ? 'bg-green-500'
                              : totalTasks <= 7
                                ? 'bg-yellow-500'
                                : totalTasks <= 12
                                  ? 'bg-orange-500'
                                  : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(100, (totalTasks / 15) * 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Status Breakdown */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">To Do</span>
                      <span className="font-medium text-foreground">{toDo}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">In Progress</span>
                      <span className="font-medium text-blue-600 dark:text-blue-400">{inProgress}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Review</span>
                      <span className="font-medium text-purple-600 dark:text-purple-400">
                        {member.status_counts?.review || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Blocked</span>
                      <span className="font-medium text-red-600 dark:text-red-400">
                        {member.status_counts?.blocked || 0}
                      </span>
                    </div>
                  </div>

                  {/* Workload Indicator */}
                  <div className="mt-4 pt-4 border-t border-border">
                    {totalTasks === 0 ? (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <TrendingDown size={14} />
                        <span>Available for tasks</span>
                      </div>
                    ) : totalTasks > 12 ? (
                      <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                        <TrendingUp size={14} />
                        <span>Overloaded</span>
                      </div>
                    ) : totalTasks > 7 ? (
                      <div className="flex items-center gap-2 text-xs text-orange-600 dark:text-orange-400">
                        <TrendingUp size={14} />
                        <span>High workload</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                        <span>Balanced workload</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamWorkloadPage;
